import { createAsyncThunk } from '@reduxjs/toolkit';
import AgentManagementService from '@/services/AgentManagementService';
import BranchesAndTeamsService from '@/services/BranchesAndTeamsService';

const handler = (name, asyncFunction) =>
  createAsyncThunk(`AgentManagement/${name}`, (arg) => asyncFunction(arg));

// `silent` refetches keep the current rows on screen while the request runs
// and replace only the page they asked for.
export const fetchAgents = handler('fetchAgents', ({ status, page, q }) =>
  AgentManagementService.listAgents({ status, page, q, includeTotals: true })
);

export const fetchCompanyBranches = handler('fetchCompanyBranches', () =>
  BranchesAndTeamsService.listBranches()
);

export const fetchCompanyTeams = handler('fetchCompanyTeams', () =>
  BranchesAndTeamsService.listTeams()
);

export const checkUserLimit = handler('checkUserLimit', () =>
  AgentManagementService.checkUserLimit()
);

// There is no endpoint that sets all of an agent's teams at once, so the diff
// goes through the per-team members endpoints. Calls are independent: on a
// partial failure the agent keeps the changes that did go through.
export const updateAgentTeams = createAsyncThunk(
  'AgentManagement/updateAgentTeams',
  async ({ agent, teamIds }, { getState, rejectWithValue }) => {
    const currentIds = agent.teams.map((team) => team.id);
    const toAdd = teamIds.filter((id) => !currentIds.includes(id));
    const toRemove = currentIds.filter((id) => !teamIds.includes(id));

    const [addResults, removeResults] = await Promise.all([
      Promise.allSettled(
        toAdd.map((teamId) =>
          BranchesAndTeamsService.addTeamMember(teamId, { userId: agent.id })
        )
      ),
      Promise.allSettled(
        toRemove.map((teamId) =>
          BranchesAndTeamsService.removeTeamMember(teamId, agent.id)
        )
      ),
    ]);

    const removedIds = toRemove.filter(
      (_, i) => removeResults[i].status === 'fulfilled'
    );
    const addedIds = toAdd.filter(
      (_, i) => addResults[i].status === 'fulfilled'
    );
    const { teams: companyTeams } = getState().AgentManagementSlice;
    const teams = [
      ...agent.teams.filter((team) => !removedIds.includes(team.id)),
      ...companyTeams.filter((team) => addedIds.includes(team.id)),
    ];

    const failure = [...addResults, ...removeResults].find(
      (result) => result.status === 'rejected'
    );
    if (failure) {
      return rejectWithValue({
        agentId: agent.id,
        teams,
        message: failure.reason?.message,
      });
    }

    return { agentId: agent.id, teams };
  }
);
